import makeStyles from "@mui/styles/makeStyles";
import { AppBar, Tabs, Tab, Tooltip } from "@mui/material";

import { setAppPath } from "../../helpers/history";
import { useEffect, useRef, useState } from "react";

const useStyles = makeStyles(theme => ({
  appBar: {
    backgroundColor: theme.palette.primary.light,
  },
  appBarSysAdmin: {
    boxShadow:
      "0px 2px 4px -1px rgba(200,0,0,0.2),0px 4px 5px 0px rgba(200,0,0,0.14),0px 1px 30px 0px rgba(200,0,0,0.4)",
  },
  tabsRoot: {
    minHeight: "unset",
    height: theme.spacing(3.5),
    "& .MuiTab-root": { transitionDuration: 100 },
    "& .MuiTab-root:hover": { transform: "translateY(-2px)" },
    "& .Mui-disabled": {
      color: theme.palette.grey[500],
      pointerEvents: "unset!important",
      textShadow: "none",
    },
  },
  tab: {
    color: theme.palette.secondary.light,
    textShadow: `1px 1px 0.5px ${theme.palette.primary.dark}, 0px 0px 2px ${theme.palette.primary.dark}`,
    minHeight: "unset",
    height: theme.spacing(3.5),
    fontWeight: 400,
  },
  tabSelected: {
    backgroundColor: theme.palette.primary.dark,
  },
  lightTooltip: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.9)",
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));

type TabProps = { toolTip: string; route: string; title: string; requiresStudy: boolean };
type AppNavProps = {
  appPath: string;
  studySelected: boolean;
  query: string;
  tabs: TabProps[];
  sysAdminMode: boolean;
};

export function AppNavBar({ appPath, studySelected, query, tabs, sysAdminMode }: AppNavProps) {
  const classes = useStyles();
  const tabRootRef = useRef<HTMLDivElement>(null);
  const [scrollButtons, setScrollButtons] = useState<boolean>(false);

  // Ensure tab scroll buttons are only visible when they should be:
  useEffect(() => {
    let debounce: NodeJS.Timeout;
    if (window.ResizeObserver && tabRootRef?.current) {
      const resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          clearTimeout(debounce);
          debounce = setTimeout(() => {
            let tabsWidth = 0;
            const tabRootWidth = entry.contentRect.width;
            const tabs = entry.target.getElementsByClassName("MuiTab-root");
            for (const tab of tabs) {
              tabsWidth += tab.clientWidth;
            }
            if (tabRootWidth && tabsWidth) {
              if (tabsWidth > tabRootWidth) {
                setScrollButtons(true);
              } else {
                setScrollButtons(false);
              }
            }
          }, 250);
        }
      });
      resizeObserver.observe(tabRootRef.current);
    }
  }, [tabRootRef]);

  return (
    <AppBar
      ref={tabRootRef}
      position="static"
      className={`${classes.appBar} ${sysAdminMode && classes.appBarSysAdmin}`}
    >
      <Tabs
        value={appPath}
        onChange={(event, value) => {
          setAppPath(value);
        }}
        variant="scrollable"
        scrollButtons={scrollButtons}
        classes={{ root: classes.tabsRoot }}
        textColor="secondary"
        indicatorColor="secondary"
      >
        {tabs.map(tab => {
          const disabled = tab.requiresStudy && !studySelected;
          return (
            <Tab
              key={tab.route}
              value={tab.route + query}
              className={classes.tab}
              label={
                <Tooltip title={tab.toolTip}>
                  <span>{tab.title}</span>
                </Tooltip>
              }
              disabled={disabled}
              classes={{ selected: classes.tabSelected }}
            />
          );
        })}
      </Tabs>
    </AppBar>
  );
}
